import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

type Section = { id: string; label: string; icon: string };

const sections: Section[] = [
  { id: "overview", label: "Coverage Overview", icon: "ri-shield-check-line" },
  { id: "brands", label: "Brand Warranties", icon: "ri-award-line" },
  { id: "covered", label: "What\'s Covered", icon: "ri-checkbox-circle-line" },
  { id: "file-claim", label: "File a Claim", icon: "ri-file-list-3-line" },
  { id: "faq", label: "Warranty FAQ", icon: "ri-question-answer-line" },
];

type BrandWarranty = {
  brand: string;
  logo: string;
  products: string;
  unit: string;
  battery: string;
  labor: string;
  notes: string;
};

const brandWarranties: BrandWarranty[] = [
  {
    brand: "EcoFlow",
    logo: "ri-leaf-line",
    products: "DELTA & RIVER Series Solar Generators",
    unit: "2 years",
    battery: "2 years (80% capacity retention)",
    labor: "Included",
    notes: "EcoFlow Extra Battery modules carry a separate 2-year warranty. Warranty service handled through EcoFlow US support portal.",
  },
  {
    brand: "Bluetti",
    logo: "ri-flashlight-line",
    products: "AC, EP & EB Series Power Stations",
    unit: "2 years",
    battery: "2 years / 3,500 cycles (AC300, AC500)",
    labor: "Included",
    notes: "Bluetti B-Series expansion batteries carry independent 2-year coverage. Extended care plans available for purchase directly through Bluetti.",
  },
  {
    brand: "Jackery",
    logo: "ri-thunder-storms-line",
    products: "Explorer Series Solar Generators",
    unit: "2 years",
    battery: "2 years",
    labor: "Included",
    notes: "Jackery SolarSaga panels carry 2-year product warranty and up to 25-year power output warranty per panel specification.",
  },
  {
    brand: "Generac",
    logo: "ri-home-gear-line",
    products: "PWRcell Home Battery & Standby Generators",
    unit: "5 years (standby) / 10 years (PWRcell)",
    battery: "10 years",
    labor: "Included for first 2 years",
    notes: "Generac standby generators require annual maintenance by a certified dealer to maintain full warranty coverage. PWRcell home battery warranty is transferable with property sale.",
  },
  {
    brand: "Champion Power",
    logo: "ri-settings-4-line",
    products: "Whole Home & Portable Generators",
    unit: "3 years",
    battery: "N/A",
    labor: "2 years",
    notes: "Champion\'s support line (1-877-338-0999) handles claims directly. On-site repair available for whole-home standby units under warranty.",
  },
  {
    brand: "Renogy",
    logo: "ri-sun-line",
    products: "Solar Panels & Charge Controllers",
    unit: "5 years (panels) / 2 years (electronics)",
    battery: "2 years",
    labor: "Included",
    notes: "Renogy solar panels carry a 25-year power output guarantee (80% minimum output through year 25). Charge controllers and inverters have separate 2-year coverage.",
  },
];

type FAQItem = { q: string; a: string };

const warrantyFAQs: FAQItem[] = [
  {
    q: "Does my warranty transfer if I sell the product?",
    a: "Transferability depends on the manufacturer. Generac\'s PWRcell home battery warranty is fully transferable with property sale. Most portable power station warranties are tied to the original purchaser. Contact us with your serial number and we\'ll confirm transfer eligibility with the manufacturer.",
  },
  {
    q: "What if I need warranty service but the manufacturer is hard to reach?",
    a: "Iron Haven Supply acts as your warranty advocate. If you\'re having difficulty reaching a manufacturer\'s support team or getting a claim resolved, email support@ironhavensupply.com with your order number and issue details — we\'ll escalate on your behalf.",
  },
  {
    q: "Does the warranty cover cosmetic damage?",
    a: "No. Cosmetic damage such as scratches, dents, and discoloration are not covered unless they were caused during shipping and reported within 48 hours of delivery. Structural damage caused by impact or dropping is also excluded.",
  },
  {
    q: "My product developed a fault after the 30-day return window — am I out of options?",
    a: "Not at all. Post-return-window defects fall under the manufacturer warranty, which typically covers 2–5 years from date of purchase. Contact us or the manufacturer directly to initiate a warranty claim — you do not have to absorb a repair cost during the warranty period.",
  },
  {
    q: "Can I get an extended warranty?",
    a: "Several brands offer optional extended care plans — EcoFlow, Bluetti, and Generac all have extended coverage programs. For standby generators, we recommend checking with us at the time of purchase. Homeowner\'s insurance riders can also provide additional accidental damage coverage beyond what manufacturer warranties include.",
  },
  {
    q: "What documentation do I need to file a claim?",
    a: "You\'ll need your original order confirmation or receipt, the product\'s serial number (usually on the bottom label or in the app), a description of the defect, and photo or video evidence of the issue. The more detail you provide upfront, the faster the claim gets resolved.",
  },
];

const claimSteps = [
  {
    step: 1,
    icon: "ri-camera-line",
    title: "Document the Issue",
    body: "Take clear photos or a short video of the defect or malfunction. If the product won\'t power on or charges incorrectly, record the behavior. Good documentation speeds up approval significantly.",
  },
  {
    step: 2,
    icon: "ri-mail-send-line",
    title: "Contact Our Support Team",
    body: "Email support@ironhavensupply.com with your order number, serial number, a description of the issue, and your photos attached. We respond within 1 business day.",
  },
  {
    step: 3,
    icon: "ri-file-check-line",
    title: "We Assess & File on Your Behalf",
    body: "Our team reviews your claim and either resolves it directly or files it with the manufacturer on your behalf. We\'ll keep you updated every step of the way.",
  },
  {
    step: 4,
    icon: "ri-truck-line",
    title: "Repair or Replacement Shipped",
    body: "Once approved, the manufacturer will dispatch a repair technician (for standby generators) or ship a replacement unit or component. Most portable power claims resolve with a replacement unit shipped within 5–10 business days.",
  },
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: warrantyFAQs.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

export default function WarrantyPage() {
  const [activeSection, setActiveSection] = useState("overview");
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      const offset = 96;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <main className="font-sans bg-white min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Navbar />

      {/* Hero */}
      <div className="pt-20 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-10 pb-12">
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-300"></i>
            <span className="text-gray-700 font-medium">Warranty Information</span>
          </nav>

          <div className="flex items-start gap-4">
            <div className="w-11 h-11 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
              <i className="ri-shield-star-line text-2xl text-amber-500"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
                Warranty Information
              </h1>
              <p className="text-gray-500 mt-2 text-base max-w-2xl">
                Every product sold by Iron Haven Supply is backed by the full manufacturer warranty. Here&apos;s exactly what&apos;s covered, for how long, and how to get a claim resolved fast.
              </p>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: "ri-shield-check-line", label: "Coverage Included", value: "All Products" },
              { icon: "ri-time-line", label: "Warranty Period", value: "Up to 10 Years" },
              { icon: "ri-customer-service-2-line", label: "Claim Support", value: "We File For You" },
              { icon: "ri-speed-line", label: "Avg. Resolution", value: "5–10 Business Days" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                  <i className={`${stat.icon} text-lg text-amber-500`}></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                  <p className="text-sm font-bold text-gray-900 leading-tight">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Layout */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Sticky sidebar */}
          <aside className="lg:w-52 shrink-0">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Jump To</p>
            <nav className="flex flex-row lg:flex-col gap-1.5 flex-wrap">
              {sections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-all cursor-pointer whitespace-nowrap w-full ${
                    activeSection === s.id
                      ? "bg-amber-500 text-white"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${s.icon} text-base`}></i>
                  </div>
                  {s.label}
                </button>
              ))}
            </nav>

            {/* Claim CTA */}
            <div className="mt-8 hidden lg:block bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg mb-3">
                <i className="ri-file-list-3-line text-lg text-amber-600"></i>
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">Need to File a Claim?</p>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">We handle it for you. Email us your order number and issue.</p>
              <a
                href="mailto:support@ironhavensupply.com"
                className="block text-center bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Start a Claim
              </a>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-14">

            {/* Overview */}
            <section id="overview">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900">Coverage Overview</h2>
              </div>

              <div className="prose prose-sm max-w-none text-gray-600 space-y-4 leading-relaxed text-sm">
                <p>
                  Every product sold by <strong className="text-gray-800">Iron Haven Supply</strong> is covered by the manufacturer&apos;s original warranty from the date of purchase. We do not sell reconditioned, gray-market, or refurbished units — every item ships new, in original packaging, directly authorized by the brand.
                </p>
                <p>
                  Warranty periods range from <strong className="text-gray-800">2 years on portable power stations</strong> to <strong className="text-gray-800">10 years on certain home battery systems</strong> (such as the Generac PWRcell). Coverage typically includes defects in materials and workmanship and, for battery products, a minimum capacity retention guarantee.
                </p>
                <p>
                  Iron Haven Supply acts as your warranty liaison. Rather than navigating manufacturer support portals alone, you can contact our team and we&apos;ll file and manage the claim on your behalf at no charge.
                </p>
              </div>

              {/* Coverage callout boxes */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: "ri-tools-line", color: "bg-emerald-50 text-emerald-600 border-emerald-100", title: "Manufacturing Defects", body: "Faults in materials or workmanship under normal use are covered for the full warranty period at no cost." },
                  { icon: "ri-battery-charge-line", color: "bg-amber-50 text-amber-600 border-amber-100", title: "Battery Capacity Guarantee", body: "Battery products guarantee a minimum capacity threshold (typically 70–80%) through the coverage period." },
                  { icon: "ri-exchange-line", color: "bg-sky-50 text-sky-600 border-sky-100", title: "Repair or Replacement", body: "At the manufacturer\'s discretion, eligible claims are resolved by repair, replacement part, or full unit swap." },
                ].map((box) => (
                  <div key={box.title} className={`border rounded-xl p-4 ${box.color}`}>
                    <div className="w-7 h-7 flex items-center justify-center mb-2">
                      <i className={`${box.icon} text-xl`}></i>
                    </div>
                    <p className="font-bold text-gray-900 text-sm mb-1">{box.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{box.body}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Brand warranties */}
            <section id="brands">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                  <i className="ri-award-line text-lg"></i>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900">Brand Warranty Terms</h2>
              </div>
              <p className="text-sm text-gray-500 mb-5">Coverage periods by brand and product category. Dates run from original purchase date.</p>

              <div className="space-y-4">
                {brandWarranties.map((bw) => (
                  <div key={bw.brand} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-200">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                        <i className={`${bw.logo} text-lg text-amber-600`}></i>
                      </div>
                      <div>
                        <p className="font-extrabold text-gray-900 text-sm">{bw.brand}</p>
                        <p className="text-xs text-gray-400">{bw.products}</p>
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Unit / Product</p>
                          <p className="text-sm font-bold text-gray-900">{bw.unit}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Battery Coverage</p>
                          <p className="text-sm font-bold text-gray-900">{bw.battery}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Labor Included</p>
                          <p className="text-sm font-bold text-gray-900">{bw.labor}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <i className="ri-information-line text-sm text-amber-500"></i>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{bw.notes}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* What's covered / not covered */}
            <section id="covered">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900">What&apos;s Covered &amp; What&apos;s Not</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Covered */}
                <div className="border border-emerald-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3.5 bg-emerald-50 flex items-center gap-2 border-b border-emerald-100">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-base text-emerald-500"></i>
                    </div>
                    <p className="font-bold text-emerald-800 text-sm">Covered Under Warranty</p>
                  </div>
                  <ul className="px-5 py-4 space-y-2.5">
                    {[
                      "Defects in materials or workmanship",
                      "Premature battery capacity loss below guaranteed threshold",
                      "Faulty electronics, displays, or control boards",
                      "Ports or connectors that fail under normal use",
                      "Inverter or BMS failures",
                      "Manufacturer assembly errors",
                      "Unit that will not power on (DOA after burn-in)",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <i className="ri-check-line text-sm text-emerald-500"></i>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Not covered */}
                <div className="border border-red-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3.5 bg-red-50 flex items-center gap-2 border-b border-red-100">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-close-circle-fill text-base text-red-500"></i>
                    </div>
                    <p className="font-bold text-red-800 text-sm">Not Covered Under Warranty</p>
                  </div>
                  <ul className="px-5 py-4 space-y-2.5">
                    {[
                      "Accidental damage or physical impact",
                      "Water or flood damage beyond rated IP rating",
                      "Damage from improper installation or wiring",
                      "Operation outside rated voltage/temperature range",
                      "Normal wear and battery degradation within spec",
                      "Cosmetic damage (scratches, dents, fading)",
                      "Damage from unauthorized modifications or repairs",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <i className="ri-close-line text-sm text-red-500"></i>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-5 bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-error-warning-line text-base text-amber-500"></i>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong className="text-gray-800">Important for standby generators:</strong> Generac and Champion whole-home standby generators require scheduled annual maintenance by a certified dealer to maintain full warranty coverage. Failure to maintain service records may affect claim eligibility.
                </p>
              </div>
            </section>

            {/* How to file a claim */}
            <section id="file-claim">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                  <i className="ri-file-list-3-line text-lg"></i>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900">How to File a Warranty Claim</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                You don&apos;t need to navigate manufacturer portals alone. Iron Haven Supply manages warranty claims on your behalf — here&apos;s the process:
              </p>

              <div className="space-y-4">
                {claimSteps.map((step, i) => (
                  <div key={step.step} className="flex items-start gap-4">
                    <div className="flex flex-col items-center shrink-0">
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-full text-white font-extrabold text-sm">
                        {step.step}
                      </div>
                      {i < claimSteps.length - 1 && (
                        <div className="w-px h-8 bg-amber-200 mt-1"></div>
                      )}
                    </div>
                    <div className="pb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${step.icon} text-base text-amber-500`}></i>
                        </div>
                        <p className="font-bold text-gray-900 text-sm">{step.title}</p>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact options */}
              <div className="mt-8 bg-gray-900 rounded-2xl px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-5">
                <div>
                  <p className="text-white font-extrabold text-base">Ready to start a warranty claim?</p>
                  <p className="text-gray-400 text-xs mt-1">Have your order number and serial number ready — we&apos;ll handle the rest.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <a
                    href="tel:8666054975"
                    className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-sm text-amber-400"></i>
                    </div>
                    (866) 605-4975
                  </a>
                  <a
                    href="mailto:support@ironhavensupply.com"
                    className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-send-line text-sm"></i>
                    </div>
                    Email a Claim
                  </a>
                </div>
              </div>
            </section>

            {/* Warranty FAQ */}
            <section id="faq">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                  <i className="ri-question-answer-line text-lg"></i>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900">Warranty FAQ</h2>
              </div>

              <div className="space-y-2">
                {warrantyFAQs.map((item, i) => (
                  <div
                    key={i}
                    className={`border rounded-xl overflow-hidden transition-all ${
                      openFAQ === i ? "border-amber-200 bg-amber-50/40" : "border-gray-200 bg-white"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className={`font-semibold text-sm pr-4 leading-snug ${openFAQ === i ? "text-amber-700" : "text-gray-900"}`}>
                        {item.q}
                      </span>
                      <div className={`w-6 h-6 flex items-center justify-center shrink-0 rounded-full transition-colors ${
                        openFAQ === i ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500"
                      }`}>
                        <i className={`text-sm transition-transform duration-200 ${openFAQ === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-5 pb-5">
                        <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Bottom links */}
            <div className="border-t border-gray-100 pt-8 flex flex-wrap items-center gap-3 text-sm">
              <span className="text-gray-400">Related pages:</span>
              <Link to="/return-policy" className="text-amber-600 hover:underline cursor-pointer font-medium">Return Policy</Link>
              <span className="text-gray-300">·</span>
              <Link to="/shipping-policy" className="text-amber-600 hover:underline cursor-pointer font-medium">Shipping Policy</Link>
              <span className="text-gray-300">·</span>
              <Link to="/faq" className="text-amber-600 hover:underline cursor-pointer font-medium">Full FAQ</Link>
              <span className="text-gray-300">·</span>
              <Link to="/contact" className="text-amber-600 hover:underline cursor-pointer font-medium">Contact Support</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
